import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { API, BACKEND_URL } from "../App";
import axios from "axios";

export default function AuthDebugPage() {
  const { user } = useAuth();
  const [debugInfo, setDebugInfo] = useState({});
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    const info = {
      currentUrl: window.location.href,
      origin: window.location.origin,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      hash: window.location.hash,
      search: window.location.search,
      hasSessionIdInHash: window.location.hash?.includes('session_id='),
      cookies: document.cookie,
      hasCookies: document.cookie.length > 0,
      hasSessionToken: document.cookie.includes('session_token'),
      userAgent: navigator.userAgent,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      platform: navigator.platform,
      backendUrl: BACKEND_URL,
      apiUrl: API,
      isLoggedIn: !!user,
      user: user ? { userId: user.user_id, email: user.email } : null,
    };
    setDebugInfo(info);
  }, [user]);

  const testBackendConnection = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setTestResults(prev => ({
        ...prev,
        healthCheck: { success: true, data: response.data }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        healthCheck: { success: false, error: error.message }
      }));
    }
  };

  const testAuthMe = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setTestResults(prev => ({
        ...prev,
        authMe: { success: true, data: response.data }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        authMe: { 
          success: false, 
          error: error.message,
          status: error.response?.status,
          data: error.response?.data
        }
      }));
    }
  };

  const testGoogleSession = async () => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setTestResults(prev => ({
        ...prev,
        googleSession: { success: false, error: 'No session_id in URL' }
      }));
      return;
    }

    try {
      const response = await axios.post(
        `${API}/auth/google-session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setTestResults(prev => ({
        ...prev,
        googleSession: { success: true, data: response.data }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        googleSession: { 
          success: false, 
          error: error.message,
          status: error.response?.status,
          data: error.response?.data
        }
      }));
    }
  };

  const clearCookies = () => {
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Auth Debug Page</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h2 className="text-lg font-semibold mb-3">Current State</h2>
          <pre className="text-xs bg-zinc-100 p-4 rounded overflow-auto max-h-96">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h2 className="text-lg font-semibold mb-3">Test Actions</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={testBackendConnection}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Test Health Endpoint
            </button>
            <button
              onClick={testAuthMe}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Test Auth /me
            </button>
            <button
              onClick={testGoogleSession}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            >
              Test Google Session
            </button>
            <button
              onClick={clearCookies}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clear All Cookies
            </button>
          </div>
        </div>

        {Object.keys(testResults).length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-3">Test Results</h2>
            <pre className="text-xs bg-zinc-100 p-4 rounded overflow-auto max-h-96">
              {JSON.stringify(testResults, null, 2)}
            </pre>
          </div>
        )}

        <div className="mt-4 text-xs text-zinc-500">
          <p>To use this page:</p>
          <ol className="list-decimal list-inside space-y-1 mt-2">
            <li>Check if cookies are set after login</li>
            <li>Test backend connectivity</li>
            <li>Test auth endpoints</li>
            <li>If you have session_id in URL, test Google session</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
